import { FC, useEffect } from 'react';
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { resetSaveCurrencySuccess, updateCurrencyRequest } from 'redux/reducers/currency/reducer';
import { getCurrencySaveLoading, getCurrencySaveSuccess } from 'redux/reducers/currency/selectors';
import { TCurrencyDialogProps } from './types';

const validationSchema = Yup.object({
	name: Yup.string().required('Name is required').max(255, 'Max 255 characters'),
	isoCode: Yup.string().required('ISO Code is required').length(3, 'Must be exactly 3 characters'),
	symbol: Yup.string().max(10, 'Max 10 characters'),
});

export const CurrencyDialog: FC<TCurrencyDialogProps> = ({ open, onClose, currencyToEdit }) => {
	const dispatch = useDispatch();

	const loading = useSelector(getCurrencySaveLoading);
	const saveSuccess = useSelector(getCurrencySaveSuccess);

	const formik = useFormik({
		initialValues: {
			name: currencyToEdit?.name ?? '',
			symbol: currencyToEdit?.symbol ?? '',
			isoCode: currencyToEdit?.isoCode ?? '',
			isSupported: currencyToEdit?.isSupported ?? false,
			isEuCbSupported: currencyToEdit?.isEuCbSupported ?? false,
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			if (!currencyToEdit) return;
			const body = {
				name: values.name,
				symbol: values.symbol,
				isSupported: values.isSupported,
				isoCode: values.isoCode,
				isEuCbSupported: values.isEuCbSupported,
			};
			dispatch(updateCurrencyRequest({ id: currencyToEdit.id, body }));
		},
	});

	useEffect(() => {
		if (saveSuccess) {
			dispatch(resetSaveCurrencySuccess());
			formik.resetForm();
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [saveSuccess]);

	const handleClose = () => {
		formik.resetForm();
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Edit Currency</DialogTitle>
			<DialogContent>
				<Box
					component="form"
					onSubmit={formik.handleSubmit}
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
				>
					<TextField
						label="Name"
						name="name"
						size="small"
						value={formik.values.name}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.name && !!formik.errors.name}
						helperText={formik.touched.name && formik.errors.name}
					/>
					<Box sx={{ display: 'flex', gap: 2 }}>
						<TextField
							label="ISO Code"
							name="isoCode"
							size="small"
							fullWidth
							value={formik.values.isoCode}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.isoCode && !!formik.errors.isoCode}
							helperText={formik.touched.isoCode && formik.errors.isoCode}
						/>
						<TextField
							label="Symbol"
							name="symbol"
							size="small"
							fullWidth
							value={formik.values.symbol}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.symbol && !!formik.errors.symbol}
							helperText={formik.touched.symbol && formik.errors.symbol}
						/>
					</Box>
					<FormControlLabel
						control={
							<Checkbox
								name="isSupported"
								checked={formik.values.isSupported}
								onChange={formik.handleChange}
							/>
						}
						label="Supported"
					/>
					<FormControlLabel
						control={
							<Checkbox
								name="isEuCbSupported"
								checked={formik.values.isEuCbSupported}
								onChange={formik.handleChange}
							/>
						}
						label="EU CB Supported"
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={() => formik.handleSubmit()}
					variant="contained"
					color="primary"
					disabled={loading}
					startIcon={loading ? <CircularProgress size={16} /> : undefined}
				>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};
